import { SELLING_STRATEGY_MAX_LENGTH } from "@repo/app-types/api/funnel";
import type {
  AdminApiMinimalPlaybook,
  PlaybookId,
} from "@repo/app-types/entities/playbook";
import { ComboboxField } from "@repo/ui/combobox/comboboxField";
import { Textarea } from "@repo/ui/Inputs/textarea";
import type { FC } from "react";
import { useAllMinimalPlaybooksQuery } from "../../../api/entities/playbook.js";

type FunnelPlaybookOption = Pick<AdminApiMinimalPlaybook, "id" | "name">;

export interface FunnelSettingsFieldsProps {
  sellingStrategy: string;
  onSellingStrategyChange: (sellingStrategy: string) => void;
  playbookIds: readonly PlaybookId[];
  onPlaybookIdsChange: (playbookIds: PlaybookId[]) => void;
  isDisabled?: boolean;
}

/** The parts of a funnel the customer edits: its selling strategy and playbooks. */
export const FunnelSettingsFields: FC<FunnelSettingsFieldsProps> = (props) => {
  const {
    sellingStrategy,
    onSellingStrategyChange,
    playbookIds,
    onPlaybookIdsChange,
    isDisabled,
  } = props;

  // A voice agent's own playbook never samples top reps.
  const { data: playbooks, isLoading: isLoadingPlaybooks } =
    useAllMinimalPlaybooksQuery({ shouldExcludeTeammateAgentPlaybooks: true });
  // A linked playbook since deleted or made a voice agent's isn't an option,
  // but stays visible so it can be removed.
  const optionIds = new Set((playbooks ?? []).map((playbook) => playbook.id));
  const options: FunnelPlaybookOption[] = [
    ...(playbooks ?? []),
    ...(isLoadingPlaybooks
      ? []
      : playbookIds
          .filter((id) => !optionIds.has(id))
          .map((id) => ({ id, name: "Unavailable playbook" }))),
  ];

  return (
    <div className="flex flex-col gap-4">
      <Textarea
        label="Selling strategy"
        description="Markdown. Every step of the analysis reads it. Editing it later doesn't change the stages and moments."
        placeholder="How this agent should sell: the offer, who it's for, the steps to a sale, and how to handle common objections."
        value={sellingStrategy}
        onChange={onSellingStrategyChange}
        maxLength={SELLING_STRATEGY_MAX_LENGTH}
        height="400px"
        isDisabled={isDisabled}
      />
      <ComboboxField<FunnelPlaybookOption, "multiple">
        label="Top rep playbooks"
        description="The funnel compares the agent with the top reps on these playbooks."
        selectionMode="multiple"
        items={options}
        getLabel={(playbook) => playbook.name}
        value={playbookIds}
        onChange={onPlaybookIdsChange}
        placeholder={
          isLoadingPlaybooks ? "Loading playbooks..." : "Select playbooks"
        }
        isDisabled={isDisabled || isLoadingPlaybooks}
      />
    </div>
  );
};
